import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { Unit } from '../models/unit.model';
import { UnitType } from '../models/unit-type.model';

@Injectable({
  providedIn: 'root'
})
export class UnitService {

  static initialized = false;

  exchangeRateDate: string;

  units: Unit[] = [];
  unitTypes: UnitType[] = [];

  // kons test temporary comment

  unitsLoaded = false;
  unitTypesLoaded = false;

  unitsLoadedNotifier = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
  ) {
  }

  private setUnitsLoaded(loaded: boolean) {
    this.unitsLoaded = loaded;
    this.checkLoaded();
  }

  private setUnitTypesLoaded(loaded: boolean) {
    this.unitTypesLoaded = loaded;
    this.checkLoaded();
  }

  private checkLoaded() {
    if (this.unitTypesLoaded && this.unitsLoaded && !this.unitsLoadedNotifier.value) {
      // Once the unit types and units are loaded, update the currency unit
      // factors with the latest exchange rate values
      this.updateCurrencyUnits().then(success => {
        if (success) {
          this.unitsLoadedNotifier.next(true);
        }
      });
    }
  }

  /**
   * Fetches unit data from the database server.
   */
  private getUnitData() {
    if (this.units && this.units.length > 0) {
      this.setUnitsLoaded(true);
    } else {
      this.http.get('/dbapi/units')
        .pipe(take(1))
        .subscribe((data: { obj: Unit[] }) => {
          if (data != null && data.obj != null && data.obj.length > 0) {
            this.units = data.obj.map(unit => new Unit(unit));
            this.setUnitsLoaded(true);
          }
        });
    }
  }

  /**
   * Fetches unit type data from the database server.
   */
  private getUnitTypeData() {
    if (this.unitTypes && this.unitTypes.length > 0) {
      this.setUnitTypesLoaded(true);
    } else {
      this.http.get('/dbapi/units/types')
        .pipe(take(1))
        .subscribe((data: { obj: UnitType[] }) => {
          if (data != null && data.obj != null && data.obj.length > 0) {
            this.unitTypes = data.obj.map(unitType => new UnitType(unitType));
            this.setUnitTypesLoaded(true);
          }
        });
    }
  }

  /**
   * Load data if it has not been loaded.
   */
  loadData() {
    if (UnitService.initialized) {
      return;
    }

    if (!this.unitTypesLoaded) {
      this.getUnitTypeData();
    }

    if (!this.unitsLoaded) {
      this.getUnitData();
    }

    UnitService.initialized = true;
  }

  /**
   * Returns all available units. Returns an empty list if none are available.
   */
  getAllUnits(): Unit[] {
    this.loadData();

    if (this.unitsLoaded) {
      return this.units;
    } else {
      return [];
    }
  }

  /**
   * Returns all units of the specified type. Returns an empty list if none are
   * available or the name is invalid.
   *
   * @param unitType name of the unit type to return units for
   */
  getUnitsByUnitTypeName(unitTypeName: string): Unit[] {
    this.loadData();

    if (this.unitsLoaded && this.unitTypesLoaded) {
      const foundType = this.unitTypes.find(unit => unit.name === unitTypeName);
      if (foundType != null) {
        return this.units.filter(unit => unit.unitType === foundType.id);
      } else {
        console.error(`invalid unit type name: ${unitTypeName}`);
        return [];
      }
    } else {
      return [];
    }
  }

  /**
   * Returns all units of the specified types. Returns an empty list if none are
   * available or the names are invalid.
   *
   * @param unitTypeNames name of the unit type to return units for
   */
  getUnitsByUnitTypeNames(unitTypeNames: string[]): Unit[] {
    this.loadData();
    if (!unitTypeNames) return [];

    const units: Unit[] = [];
    for (const unitType of unitTypeNames) {
      units.push(... this.getUnitsByUnitTypeName(unitType));
    }
    return units;
  }

  /**
   * Returns all units of the specified type. Returns an empty list if none are
   * found or the id is invalid.
   *
   * @param unitTypeId id of the unit type to return units for
   */
  getUnitsByUnitTypeId(unitTypeId: string): Unit[] {
    this.loadData();

    if (this.unitsLoaded && this.unitTypesLoaded) {
      const foundType = this.unitTypes.find(unit => unit.id === unitTypeId);
      if (foundType != null) {
        return this.units.filter(unit => unit.unitType === unitTypeId);
      } else {
        console.error(`invalid unit type id: ${unitTypeId}`);
        return [];
      }
    } else {
      return [];
    }
  }

  /**
   * Returns the currency units with the conversion factor updated with the
   * exchange rates for the given date. If no date is provided, the latest
   * available rates will be used.
   *
   * @param unitTypeId id of the date to fetch exchange rates for
   */
  async getCurrencyUnits(dateId?: string): Promise<Unit[]> {
    await this.updateCurrencyUnits(dateId);

    return new Promise((resolve, reject) => {
      resolve(this.getUnitsByUnitTypeName('Currency'));
    });
  }

  /**
   * Returns all the currency units with the conversion factor updated with the
   * exchange rates for the given date. If no date is provided, the latest
   * available rates will be used.
   * @param dataId
   */
  async getAllCurrencyUnits(dataId?: string): Promise<Unit[]> {
    await this.updateCurrencyUnits(dataId);
    return new Promise((resolve, reject) => {
      resolve(this.getUnitsByUnitTypeNames(['Currency', 'Investment', 'CPI']));
    });
  }

  /**
   * Updates the currency units conversion factor with the exchange rates for
   * the given date. If no date is provided, the latest available rates will
   * be used.
   *
   * @param unitTypeId id of the date to fetch exchange rates for
   */
  async updateCurrencyUnits(dateId?: string): Promise<boolean> {
    let data: any;
    if (dateId == null) {
      data = await this.http.get('/dbapi/currency/rates/latest').toPromise();
    } else {
      data = await this.http.get('/dbapi/currency/rates/' + dateId).toPromise();
    }
    return new Promise((resolve, reject) => {
      if (data != null && data['obj'] != null) {
        const currencyUnits = this.getUnitsByUnitTypeNames(['Currency', 'Investment', 'CPI']);
        const exchangeRates = data['obj'].rates;
        this.exchangeRateDate = data['obj'].date;

        for (const unit of currencyUnits) {
          // % units for Investment and CPI unit types
          if (unit?.id === '000000000000000000002101' || unit?.id === '000000000000000000002201' || !unit?.displayName) {
            continue;
          }
          const curName = unit.displayName.slice(0, 3);
          unit['factor'] = `V/${exchangeRates[curName]}`;
        }
        resolve(true);
      }
    });
  }

  /**
   * Returns the date of the exchange rate currently used by the currency units.
   * Date is in the format yyyy-MM-dd
   */
  getExchangeRateDate(): string {
    return this.exchangeRateDate;
  }

  /**
   * Returns the unit matching the given id. Returns null if a matching unit is
   * not found.
   *
   * @param unitId id of the unit to find
   */
  getUnitById(unitId: string): Unit {
    this.loadData();

    if (this.unitsLoaded) {
      return this.units.find(unit => unit.id === unitId);
    } else {
      return null;
    }
  }

  /**
   * Returns the unit matching the given name. Returns null if a matching unit
   * is not found.
   *
   * @param unitName name of the unit to find
   */
  getUnitByName(unitName: string): Unit {
    this.loadData();

    if (this.unitsLoaded) {
      return this.units.find(unit => unit.displayName === unitName);
    } else {
      return null;
    }
  }

  /**
   * Returns the unit of the specified type and matching the given name. 
   * Returns null if not found.
   *
   * @param unitName name of the unit
   * @param unitType name of the unit type
   */
  getUnitByNameAndUnitTypeName(unitName: string, unitTypeName: string): Unit {
    this.loadData();

    if (this.unitsLoaded && this.unitTypesLoaded) {
      const foundType = this.unitTypes.find(unit => unit.name === unitTypeName);
      if (foundType != null) {
        return this.units.find(unit => unit.unitType === foundType.id && unit.displayName === unitName);
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  /**
   * Returns a normalized value (in base unit) for a given UnitValue object
   * @param uv: {unit: string, value: number}
   */
  getValueInBaseUnit(uv: { unit: string, value: number }) {
    this.loadData();
    if (uv && uv.unit && uv.value) {
      const unit = this.getUnitById(uv.unit);
      return unit.convertToBase(uv.value);
    }
    console.error('Unit conversion failed');
    return 0;
  }
}
